import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { projectsApi, timeEntriesApi } from '@/api/endpoints'
import type { ImportPreview, ImportResult, ImportRow, ImportRowStatus, Project } from '@/api/types'
import { Button, Card, Field, Select } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { formatDuration } from '@/utils/format'

const HEADER_EXAMPLE =
  '"Description","Billable","Duration","Member","Email","Project","Tags","Start date","Start time","Stop date","Stop time"'

/** Delší soubory neukazujeme celé — v tabulce jde hlavně o kontrolu, ne o čtení. */
const ROW_LIMIT = 200

const STATUS: Record<ImportRowStatus, { label: string; done: string; className: string }> = {
  OK: { label: 'K importu', done: 'Naimportováno', className: 'bg-emerald-50 text-emerald-700' },
  DUPLICATE: { label: 'Duplicita', done: 'Přeskočeno', className: 'bg-amber-50 text-amber-700' },
  ERROR: { label: 'Chyba', done: 'Chyba', className: 'bg-red-50 text-red-700' },
}

export default function ImportPage() {
  const [projects, setProjects] = useState<Project[]>([])
  const [csv, setCsv] = useState('')
  const [fileName, setFileName] = useState('')
  const [projectMap, setProjectMap] = useState<Record<string, string>>({})
  const [skipDuplicates, setSkipDuplicates] = useState(true)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [onlyProblems, setOnlyProblems] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    projectsApi.list().then(setProjects)
  }, [])

  // Náhled přepočítáváme po každé změně vstupu i mapování — ať to, co je vidět,
  // odpovídá tomu, co se opravdu uloží.
  useEffect(() => {
    if (!csv.trim()) return setPreview(null)
    let current = true
    setBusy(true)
    setError(null)
    timeEntriesApi
      .importPreview({ csv, projectMap, skipDuplicates })
      .then((data) => current && setPreview(data))
      .catch((err) => {
        if (!current) return
        setPreview(null)
        setError(errorMessage(err, 'Soubor se nepodařilo přečíst.'))
      })
      .finally(() => current && setBusy(false))
    return () => {
      current = false
    }
  }, [csv, projectMap, skipDuplicates])

  const pickFile = async (file: File | undefined) => {
    if (!file) return
    reset()
    setFileName(file.name)
    setCsv(await file.text())
  }

  const reset = () => {
    setCsv('')
    setFileName('')
    setProjectMap({})
    setPreview(null)
    setResult(null)
    setOnlyProblems(false)
    setError(null)
  }

  const runImport = async () => {
    setBusy(true)
    setError(null)
    try {
      setResult(await timeEntriesApi.import({ csv, projectMap, skipDuplicates }))
      setPreview(null)
    } catch (err) {
      setError(errorMessage(err, 'Import se nepovedl.'))
    } finally {
      setBusy(false)
    }
  }

  const rows = result?.rows ?? preview?.rows ?? []
  const shown = (onlyProblems ? rows.filter((r) => r.status !== 'OK') : rows).slice(0, ROW_LIMIT)
  const hidden = (onlyProblems ? rows.filter((r) => r.status !== 'OK').length : rows.length) - shown.length

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Import záznamů</h1>

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50">
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            Vybrat CSV…
          </label>
          {fileName && <span className="text-sm text-slate-600">{fileName}</span>}
          {(csv || result) && (
            <Button variant="ghost" onClick={reset}>
              Vyčistit
            </Button>
          )}
          <label className="ml-auto inline-flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={skipDuplicates}
              onChange={(e) => setSkipDuplicates(e.target.checked)}
              disabled={!!result}
            />
            Přeskočit už naimportované
          </label>
        </div>

        {!csv && !result && (
          <div className="mt-4 space-y-2 text-sm text-slate-500">
            <p>Očekávaná hlavička (export z Toggl Tracku):</p>
            <code className="block overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              {HEADER_EXAMPLE}
            </code>
            <p>
              Sloupce se hledají podle názvu, na pořadí nezáleží. Povinné jsou <strong>Project</strong>,{' '}
              <strong>Start date</strong>, <strong>Start time</strong> a k tomu buď <strong>Stop date/time</strong>,
              nebo <strong>Duration</strong>. Časy se čtou v pražském čase, <em>Billable</em> a <em>Tags</em> se
              ignorují (fakturace se řídí projektem). Cizí e-mail v <em>Email</em> může importovat jen admin.
            </p>
          </div>
        )}
      </Card>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      )}

      {preview && preview.unknownProjects.length > 0 && (
        <Card>
          <h2 className="mb-1 font-semibold">Nespárované projekty</h2>
          <p className="mb-3 text-sm text-slate-500">
            Tyhle názvy z CSV v tajmtrekru nejsou. Přiřaď je ručně, nebo je nejdřív založ v Projektech.
          </p>
          <div className="space-y-3">
            {preview.unknownProjects.map((name) => (
              <div key={name} className="flex flex-wrap items-end gap-3">
                <span className="min-w-40 pb-2 text-sm font-medium">{name}</span>
                <div className="min-w-64 flex-1">
                  <Field label="Importovat do">
                    <Select
                      value={projectMap[name] ?? ''}
                      onChange={(e) =>
                        setProjectMap((prev) => {
                          const next = { ...prev }
                          if (e.target.value) next[name] = e.target.value
                          else delete next[name]
                          return next
                        })
                      }
                    >
                      <option value="">Vyber projekt…</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.clientName} · {p.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {(preview || result) && (
        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-semibold">{result ? 'Výsledek importu' : 'Náhled'}</h2>
            <div className="flex items-center gap-4 text-sm">
              {result ? (
                <>
                  <span className="text-emerald-700">Naimportováno {result.imported}</span>
                  <span className="text-amber-700">Přeskočeno {result.skipped}</span>
                  <span className="text-red-700">Chyb {result.failed}</span>
                </>
              ) : (
                <>
                  <span className="text-slate-500">Řádků {preview!.total}</span>
                  <span className="text-emerald-700">K importu {preview!.okCount}</span>
                  <span className="text-amber-700">Duplicit {preview!.duplicateCount}</span>
                  <span className="text-red-700">Chyb {preview!.errorCount}</span>
                </>
              )}
            </div>
          </div>

          <label className="mb-3 inline-flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
            Jen problémové řádky
          </label>

          {shown.length === 0 ? (
            <p className="text-sm text-slate-400">Žádné řádky k zobrazení.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-400">
                    <th className="py-1">Řádek</th>
                    <th className="py-1">Stav</th>
                    <th className="py-1">Datum</th>
                    <th className="py-1">Čas</th>
                    <th className="py-1 text-right">Trvání</th>
                    <th className="py-1">Projekt</th>
                    <th className="py-1">Popis</th>
                    <th className="py-1">Poznámka</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => (
                    <Row key={row.line} row={row} done={!!result} />
                  ))}
                </tbody>
              </table>
              {hidden > 0 && <p className="pt-2 text-xs text-slate-400">…a dalších {hidden} řádků</p>}
            </div>
          )}

          {preview && (
            <div className="mt-4 flex items-center justify-end gap-3">
              {preview.errorCount > 0 && (
                <span className="text-sm text-slate-500">Chybné řádky se přeskočí.</span>
              )}
              <Button onClick={runImport} disabled={busy || preview.okCount === 0}>
                {busy ? 'Pracuji…' : `Importovat ${preview.okCount} záznamů`}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

function Row({ row, done }: { row: ImportRow; done: boolean }) {
  const status = STATUS[row.status]
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="py-1.5 text-slate-400">{row.line}</td>
      <td className="py-1.5">
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${status.className}`}>
          {done ? status.done : status.label}
        </span>
      </td>
      <td className="py-1.5 text-slate-500">{row.startTime ? dayjs(row.startTime).format('D. M. YYYY') : '—'}</td>
      <td className="py-1.5 font-mono text-xs tabular-nums text-slate-500">
        {row.startTime ? dayjs(row.startTime).format('HH:mm') : '—'}
        {row.endTime ? `–${dayjs(row.endTime).format('HH:mm')}` : ''}
      </td>
      <td className="py-1.5 text-right font-mono tabular-nums">
        {row.durationSecs != null ? formatDuration(row.durationSecs) : '—'}
      </td>
      <td className="py-1.5">{row.projectLabel ?? row.csvProject ?? '—'}</td>
      <td className="py-1.5">{row.description}</td>
      <td className="py-1.5 text-slate-500">{row.message}</td>
    </tr>
  )
}
